import { artOfSkill, ELEMENT_INFO, type SkillDef } from '@nine/shared';
import { mix } from '../icons/pix';
import { drawChip, drawGem, chipOf } from './chips';
import { INK, Layer, type Size } from './grid';
import { drawPlate, type MoveKind, type Shape, schoolMark } from './plate';
import { type Ctx, SCENES, type Tones } from './scenes';
import './scenes2';
import './scenes3';
import './scenes4';
// C0-VFX: the golden dragon palm's pictures.
import './scenesDragon';
import { RECIPES } from './recipes';

const MONSTER = '#c8603a';

export const tonesOf = (c: string): Tones => ({
  hi: mix(c, '#ffffff', 0.8),
  l: mix(c, '#ffffff', 0.45),
  c,
  d: mix(c, INK, 0.42),
});

/** The default picture for an fx when a move has no recipe of its own. */
const FX_SCENE: Readonly<Record<string, string>> = {
  bolt: 'bolt', pierce: 'pierce', nine: 'nine', needles: 'needles', palm: 'palm', vajra: 'vajra', grab: 'grab',
  whirl: 'whirl', wind: 'wind', breath: 'breath', taiji: 'taiji', calm: 'calm', aura: 'aura', chain: 'chain',
  crush: 'crush', quake: 'quake', cleave: 'cleave', sabre: 'sabre', slash: 'slash', bite: 'bite', blood: 'blood',
  bloodstorm: 'bloodstorm', shadow: 'shadow', brush: 'brush', ink: 'ink', seal: 'seal', snow: 'snow',
  snowdash: 'snowdash', blizzard: 'blizzard', snowstep: 'snowstep', icebreak: 'icebreak', snowpride: 'snowpride',
  snowblink: 'snowblink', bell: 'bell', ring: 'ring', dgHarpoon: 'dgHarpoon', dgSleep: 'dgSleep', dgChain: 'dgChain',
  dgWhirlpool: 'dgWhirlpool', dgIron: 'dgIron',
};

export interface Recipe {
  scene: string;
  v?: number;
  /** Flip the picture left-right (the light pass runs after, so light stays upper-right). */
  flip?: boolean;
  /** Draw in another colour than the art's element (monsters, blood, snow). */
  tone?: string;
}

export function recipeOf(s: Pick<SkillDef, 'id' | 'fx'>): Recipe {
  return RECIPES[s.id] ?? { scene: FX_SCENE[s.fx] ?? 'ring' };
}

/** Everything a move icon is built from, for the sheets too. */
export function iconParts(s: SkillDef): { kind: MoveKind; ground: string; el: string; school: string; rec: Recipe } {
  const art = artOfSkill(s.id);
  const el = art ? ELEMENT_INFO[art.element].color : MONSTER;
  return {
    kind: s.kind as MoveKind,
    el,
    ground: mix(INK, el, 0.34),
    school: art ? art.school : '',
    rec: recipeOf(s),
  };
}

/** The finished icon for a move at a native size (16, 24, 32 or 48). */
export function drawSkillIcon(s: SkillDef, n: Size = 24, shape: Shape = 'round'): Layer {
  const { kind, ground, el, school, rec } = iconParts(s);
  const tone = rec.tone ?? el;
  const { layer, inner } = drawPlate(kind, rec.tone && !school ? mix(INK, tone, 0.34) : ground, n, shape);
  if (n >= 24) {
    const mark = schoolMark(school, mix(ground, '#ffffff', 0.13), n);
    for (let i = 0; i < layer.d.length; i++) if (mark.d[i] && inner[i]) layer.d[i] = mark.d[i]!;
  }
  let pic = new Layer(n);
  const ctx: Ctx = { t: tonesOf(tone), v: rec.v ?? 0 };
  (SCENES[rec.scene] ?? SCENES.ring!)(pic, ctx);
  if (rec.flip) pic = pic.mirror();
  pic.light();
  pic.outline();
  layer.stamp(pic);
  const chip = chipOf(s);
  if (chip) drawChip(layer, chip);
  if (shape === 'square') drawGem(layer, kind);
  return layer;
}
